import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Immutable from 'immutable';
import {
  clearItineraries,
  clearItinerarySuggestions,
  postItinerarySuggestionAction,
} from '../../../../../actions';
import FormFooter from '../../../../common/forms/FormFooter';
import Table from '../../../../common/Table';
import { filterMethodCaseInsensitive } from '../../../../../utils/filter-methods';
import Modal from '../../../../common/modal/Modal';
import ItinerariesForSchedulingForm from './ItinerariesForSchedulingForm';
import {
  COLOR,
  TRIP_CIRCUIT_STATUS,
  TRIP_CIRCUIT_STATUS_OPTIONS,
} from '../../../../../config/constants';
import { orderBy } from '../../../../../utils/array';
import ItinerariesForCrewCabineForm from './ItinerariesForCrewCabineForm';
import ItineraryForSchedulingModal from './ItineraryForSchedulingModal';
import ItinerarySuggestionDetailModal from './ItinerarySuggestionDetailModal';
import ItinerarySuggestionTraceModal from './ItinerarySuggestionTraceModal';
import ItinerarySuggestionActionForm from './ItinerarySuggestionActionForm';
import generateItinerariesForSchedulingTableColumns from './generateItinerariesForSchedulingTableColumns';
import ItineraryFoodForm from '../itinerary-food/ItineraryFoodForm';
import { tzNormalizeDate } from '../../../../../utils/date';

const generateStyle = (row) => {
  let background = '';

  if (row) {
    const {
      original: {
        highligth,
        highligthCrewCabine,
        highligthAlimentacion,
        highlightSuggestion,
      },
    } = row;

    if (highlightSuggestion) {
      background = COLOR.info;
    } else if (highligth || highligthCrewCabine || highligthAlimentacion) {
      background = COLOR.success;
    }
  }

  return { background };
};

const ItinerariesForSchedulingTable = ({
  onHandleBusAssignment,
  dispatchClearItineraries,
  loading,
  itineraries,
  itinerarySuggestionList,
  dispatchClearItinerarySuggestions,
  selectedDataRef,
  dispatchPostItinerarySuggestionAction,
  setIsFilteredData,
  setVisibleItineraries,
  visibleColumnIds,
  tableKey,
}) => {
  useEffect(
    () => () => {
      dispatchClearItineraries();
      dispatchClearItinerarySuggestions();
    },
    [],
  );

  const [data, setData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('Registro de Datos');
  const [modalBody, setModalBody] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [selectedData, setSelectedData] = useState([]);

  useEffect(() => {
    // eslint-disable-next-line no-param-reassign
    selectedDataRef.current = selectedData;
  }, [selectedData]);

  const updateDataWithSuggestion = () => {
    const newData = data.map((row) => {
      const findSuggestion = itinerarySuggestionList.find(
        (suggestion) => suggestion.itineraryId === row.id,
      );
      if (findSuggestion && findSuggestion.suggestionFound) {
        return {
          ...row,
          companyBusId: {
            value: findSuggestion.busId,
            label: findSuggestion.companyBusId,
          },
          tripCircuitStatus: {
            value: TRIP_CIRCUIT_STATUS[findSuggestion.tripCircuitStatus].value,
            label: TRIP_CIRCUIT_STATUS[findSuggestion.tripCircuitStatus].label,
          },
          highlightSuggestion: true,
          suggestionDetailList: findSuggestion.selection
            ? findSuggestion.selection.reasons
            : [],
          previousItinerary: findSuggestion.previousItinerary,
          previousSuggestedItinerary: findSuggestion.previousSuggestedItinerary,
          previousProgrammedItinerary:
            findSuggestion.previousProgrammedItinerary,
          waitTimeViolation: findSuggestion.waitTimeViolation,
          busAssignmentScheduleId: findSuggestion.busAssignmentScheduleId,
          suggestionTrace: findSuggestion.trace,
        };
      }
      return {
        ...row,
        highlightSuggestion: false,
        waitTimeViolation: null,
        busAssignmentScheduleId: null,
        suggestionTrace: findSuggestion ? findSuggestion.trace : null,
      };
    });

    setData(newData);

    const currentSuggestionIds = new Set(
      itinerarySuggestionList.map((s) => s.itineraryId),
    );

    const newSelectedData = [
      ...selectedData.filter(
        (row) => !row.highlightSuggestion || !currentSuggestionIds.has(row.id),
      ),
      ...newData.filter((row) => row.highlightSuggestion),
    ];

    setSelectedData(newSelectedData);
  };

  useEffect(() => {
    if (!itinerarySuggestionList.length > 0) {
      return;
    }
    updateDataWithSuggestion();
  }, [itinerarySuggestionList]);

  const didUpdate = () => {
    const content = itineraries.get('content') || [];

    const newContent = content.map((row) => ({
      ...row,
      highligth: false,
      highligthCrewCabine: false,
      highligthAlimentacion: false,
      highlightSuggestion: false,
      itineraryScheduleFoodRequestList:
        row.itineraryScheduleFoodRequestList || [],
      companyBusId: row.companyBusId
        ? {
            value: null,
            label: row.companyBusId,
          }
        : null,
    }));

    if (newContent.length !== data.length) {
      const sortedData = orderBy(newContent, [
        'departuretime',
        'sourceLocationName',
        'destinationLocationName',
      ]);
      setData(sortedData);
      setOriginalData(sortedData);
      setSelectedData([]);
      dispatchClearItinerarySuggestions();
    }
  };

  useEffect(() => {
    didUpdate();
  }, [itineraries]);

  const onShowModal = () => setShowModal(true);

  const onCloseModal = (index) => {
    // Hack to put focus in next confirm button
    // TODO remove when upgrade dependencies: reactstrap and react-table
    const buttonFocus = document.getElementById(
      `btnConfirm${index || selectedRowIndex}`,
    );
    if (buttonFocus) buttonFocus.focus();

    setShowModal(false);
    setSelectedRowIndex(null);
  };

  const getDataIndex = ({ id }) => data.findIndex((row) => row.id === id);

  const onSubmitForm = (formValues, index) => {
    const itineraryIndex = getDataIndex({
      id: formValues.id,
    });
    const newData = [...data];
    newData[itineraryIndex] = {
      ...newData[itineraryIndex],
      companyBusId: formValues.companyBusId,
      tripCircuitStatus: formValues.tripCircuitStatus,
      highligth: true,
      maintenanceReason: formValues.maintenanceReason || null,
    };
    setSelectedData([...selectedData, newData[itineraryIndex]]);
    setData(newData);
    onCloseModal(index + 1);
  };

  const onSubmitCrewCabineForm = (formValues, index) => {
    const itineraryIndex = getDataIndex({
      id: formValues.id,
    });

    const newData = [...data];
    newData[itineraryIndex] = {
      ...newData[itineraryIndex],
      cabinCrewList: formValues.cabinCrewIdList.map((cabinCrew) => ({
        id: cabinCrew.value,
        label: cabinCrew.label,
      })),
      highligthCrewCabine: true,
    };
    setSelectedData([...selectedData, newData[itineraryIndex]]);
    setData(newData);
    onCloseModal(index + 1);
  };

  const onSubmitFoodForm = (formValues, index) => {
    const itineraryIndex = getDataIndex({
      id: formValues.id,
    });

    const newData = [...data];

    const itineraryScheduleFoodRequestList = (
      formValues.itineraryScheduleFoodRequestList || []
    ).map((request) => ({
      ...request,

      foodIds: (request.foodIds || []).map((food) => food.value),

      scheduleDeliveredDate: request.scheduleDeliveredDate
        ? tzNormalizeDate({
            date: request.scheduleDeliveredDate,
          })
        : null,
    }));

    newData[itineraryIndex] = {
      ...newData[itineraryIndex],
      itineraryScheduleFoodRequestList,
      highligthAlimentacion: true,
    };

    setSelectedData([...selectedData, newData[itineraryIndex]]);
    setData(newData);
    onCloseModal(index + 1);
  };

  const generateInitialValues = ({ tripCircuitStatus, id }) => ({
    tripCircuitStatus: tripCircuitStatus || TRIP_CIRCUIT_STATUS_OPTIONS[0],
    id,
  });

  const handleConfirm = (row, index) => {
    setSelectedRowIndex(index);

    const { passengerSeatCapacity } = row;

    const form = (
      <ItinerariesForSchedulingForm
        onSubmit={(rowElement) => onSubmitForm(rowElement, index)}
        initialValues={generateInitialValues(row)}
        passengerSeatCapacity={passengerSeatCapacity}
      />
    );

    const body = <ItineraryForSchedulingModal row={row} body={form} />;

    setModalTitle('Registro de Datos para Programar');
    setModalBody(body);
    onShowModal(true);
  };

  const generateCabinCrewInitialValues = ({ cabinCrewList, id }) => ({
    cabinCrewIdList: cabinCrewList.map((cabinCrew) => ({
      value: cabinCrew.cabinCrewId || cabinCrew.id,
      label: cabinCrew.cabinCrewName || cabinCrew.label,
    })),
    id,
  });

  const handleConfirmCabineCrew = (row, index) => {
    setSelectedRowIndex(index);

    const form = (
      <ItinerariesForCrewCabineForm
        onSubmit={(rowElement) => onSubmitCrewCabineForm(rowElement, index)}
        initialValues={generateCabinCrewInitialValues(row)}
      />
    );

    const body = <ItineraryForSchedulingModal row={row} body={form} />;
    setModalTitle('Registro de Datos para Programar');
    setModalBody(body);
    onShowModal(true);
  };

  const handleConfirmFood = (row, index) => {
    setSelectedRowIndex(index);

    const currentFoodRequestList = row.itineraryScheduleFoodRequestList || [];

    const initialFoodRequestList =
      currentFoodRequestList.length > 0
        ? currentFoodRequestList.map((request) => ({
            ...request,
            scheduleDeliveredDate:
              request.scheduleDeliveredDate || row.departuretime,
          }))
        : [
            {
              scheduleDeliveredDate: row.departuretime,
            },
          ];

    const form = (
      <ItineraryFoodForm
        handleProcess={(formValues) =>
          onSubmitFoodForm(
            {
              ...formValues,
              id: row.id,
            },
            index,
          )
        }
        initialValues={{
          id: row.id,
          itineraryScheduleFoodRequestList: initialFoodRequestList,
        }}
      />
    );

    const body = <ItineraryForSchedulingModal row={row} body={form} />;

    setModalTitle('Registro de Alimentación');
    setModalBody(body);
    onShowModal(true);
  };

  const clearRow = (index) => {
    const newData = [...data];
    newData[index] = originalData[index];
    setSelectedData(
      selectedData.filter((rowElement) => rowElement.id !== newData[index].id),
    );
    setData(newData);
    setSelectedRowIndex(null);
  };

  const handleSubmit = (formValues, row) => {
    const { busAssignmentScheduleId } = row;
    const newFormValues = {
      ...formValues,
      itineraryId: row.id,
      registeredBusId: row.companyBusId.value,
      busAssignmentScheduleId,
    };
    dispatchPostItinerarySuggestionAction(newFormValues);
    const index = getDataIndex(row);
    clearRow(index);
    setShowModal(false);
    setSelectedRowIndex(null);
    setModalBody(null);
  };

  const handleCancel = () => {
    setShowModal(false);
    setSelectedRowIndex(null);
    setModalBody(null);
  };

  const handleClear = (row) => {
    const index = getDataIndex(row);
    if (!row.highlightSuggestion) {
      clearRow(index);
      return;
    }

    const body = (
      <ItinerarySuggestionActionForm
        onSubmit={(formValues) => handleSubmit(formValues, row)}
        onCancel={handleCancel}
      />
    );
    setModalTitle('Detalle de la Anulación de la Sugerencia');
    setModalBody(body);
    setShowModal(true);
  };

  const handleClearCabineCrew = (row) => {
    const index = getDataIndex(row);
    const newData = [...data];
    newData[index] = originalData[index];
    setSelectedData(
      selectedData.filter((rowElement) => rowElement.id !== newData[index].id),
    );
    setData(newData);
    setSelectedRowIndex(null);
  };

  const handleClearFood = (row) => {
    const index = getDataIndex(row);
    const newData = [...data];

    newData[index] = {
      ...newData[index],
      itineraryScheduleFoodRequestList: [],
      highligthAlimentacion: false,
    };

    setSelectedData(
      selectedData.filter((rowElement) => rowElement.id !== newData[index].id),
    );

    setData(newData);
    setSelectedRowIndex(null);
  };

  const modal = (
    <Modal
      show={showModal}
      title={modalTitle}
      onClickClose={onCloseModal}
      body={modalBody}
      size="lg"
    />
  );

  const saveButtonDisabled =
    loading ||
    !data.find(
      (row) =>
        row.highligth ||
        row.highligthCrewCabine ||
        row.highligthAlimentacion ||
        row.highlightSuggestion,
    );

  const handleAdditionalButton = (itinerary) => {
    const body = (
      <ItinerarySuggestionDetailModal
        suggestionDetailList={itinerary.suggestionDetailList}
        previousProgrammedItinerary={itinerary.previousProgrammedItinerary}
        previousSuggestedItinerary={itinerary.previousSuggestedItinerary}
        previousItinerary={itinerary.previousItinerary}
      />
    );

    setModalTitle(`Detalle de la sugerencia - ${itinerary.id}`);
    setModalBody(body);
    setShowModal(true);
  };

  const handleShowTraceModal = (itinerary) => {
    const index = getDataIndex(itinerary);
    const suggested = itinerary.highlightSuggestion;
    const body = (
      <ItinerarySuggestionTraceModal
        trace={itinerary.suggestionTrace}
        row={itinerary}
        suggested={suggested}
        onManualAssign={() => handleConfirm(itinerary, index)}
        onClose={onCloseModal}
      />
    );

    setModalTitle(
      suggested
        ? `Cómo se eligió el bus - ${
            itinerary.companyBusId ? itinerary.companyBusId.label : ''
          }`
        : 'Por qué no se sugirió un bus',
    );
    setModalBody(body);
    setShowModal(true);
  };

  const additionalButton = (itinerary) => {
    if (itinerarySuggestionList.length > 0 && itinerary.highlightSuggestion) {
      return (
        <>
          <i
            tabIndex="0"
            role="button"
            className="fa fa-commenting-o text-info"
            id={`btnConfirm${itinerary.id}`}
            onClick={() => handleAdditionalButton(itinerary)}
            onKeyDown={() => handleAdditionalButton(itinerary)}
          />
          {itinerary.suggestionTrace && (
            <i
              tabIndex="0"
              role="button"
              className="fa fa-filter text-info ml-1"
              id={`btnTrace${itinerary.id}`}
              onClick={() => handleShowTraceModal(itinerary)}
              onKeyDown={() => handleShowTraceModal(itinerary)}
            />
          )}
        </>
      );
    }

    if (itinerary.suggestionTrace) {
      return (
        <i
          tabIndex="0"
          role="button"
          className="fa fa-exclamation-triangle text-warning"
          id={`btnNoSuggestion${itinerary.id}`}
          onClick={() => handleShowTraceModal(itinerary)}
          onKeyDown={() => handleShowTraceModal(itinerary)}
        />
      );
    }

    return null;
  };

  return (
    <div className="mt-4">
      <Table
        key={tableKey}
        manual={false}
        columns={generateItinerariesForSchedulingTableColumns({
          handleConfirm,
          handleClear,
          handleConfirmCabineCrew,
          handleClearCabineCrew,
          handleConfirmFood,
          handleClearFood,
          additionalButton,
          visibleColumnIds,
        })}
        data={data}
        filterable
        defaultFilterMethod={filterMethodCaseInsensitive}
        loading={loading}
        generateStyle={generateStyle}
        onFilteredDataChange={(visibleData, filtered) => {
          setIsFilteredData(filtered.length > 0);
          setVisibleItineraries(visibleData);
        }}
      />
      <div className="mt-3">
        <FormFooter
          saveButtonAction={() => onHandleBusAssignment(selectedData)}
          saveButtonDisabled={saveButtonDisabled}
          saveButtonText="Asignar buses"
          saveButtonType="button"
        />
      </div>
      {modal}
    </div>
  );
};

const mapStateToProps = ({ ItineraryUnit }) => ({
  loading: ItineraryUnit.Itinerary.getIn(['all', 'loading']),
  itineraries: ItineraryUnit.Itinerary.getIn(['all', 'content']),
  itinerarySuggestionList: ItineraryUnit.Itinerary.getIn([
    'itinerarySuggestions',
  ]),
});

const mapDispatchToProps = {
  dispatchClearItineraries: clearItineraries,
  dispatchClearItinerarySuggestions: clearItinerarySuggestions,
  dispatchPostItinerarySuggestionAction: postItinerarySuggestionAction,
};

ItinerariesForSchedulingTable.propTypes = {
  loading: PropTypes.bool,
  dispatchClearItineraries: PropTypes.func.isRequired,
  onHandleBusAssignment: PropTypes.func.isRequired,
  itineraries: PropTypes.instanceOf(Immutable.Map).isRequired,
  itinerarySuggestionList: PropTypes.arrayOf(
    PropTypes.shape({
      busId: PropTypes.number.isRequired,
      itineraryId: PropTypes.number.isRequired,
      companyBusId: PropTypes.string.isRequired,
    }),
  ),
  dispatchClearItinerarySuggestions: PropTypes.func.isRequired,
  selectedDataRef: PropTypes.shape({
    current: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
      }),
    ).isRequired,
  }).isRequired,
  dispatchPostItinerarySuggestionAction: PropTypes.func.isRequired,
  setVisibleItineraries: PropTypes.func.isRequired,
  setIsFilteredData: PropTypes.func.isRequired,
  visibleColumnIds: PropTypes.arrayOf(PropTypes.string),
  tableKey: PropTypes.number,
};

ItinerariesForSchedulingTable.defaultProps = {
  loading: false,
  itinerarySuggestionList: [],
  visibleColumnIds: [],
  tableKey: 0,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ItinerariesForSchedulingTable);
